/**
 * 汇率折算（**纯函数，零依赖**）。
 *
 * 背景（用户 2026-10-03 决策）：概览类页面要统一折合成 USD 看多店合计；但
 * **涉及广告花费调整时必须用广告结算货币**。因此本模块只做「读取时折算」，
 * 原币金额在任何地方都不会被改写。
 *
 * 三条纪律：
 *  1. **金额整数分 + 十进制汇率的精确乘除**。汇率以十进制**字符串**传入（就是
 *     Postgres `numeric(20,10)` 的文本形态），内部换算成 10^10 缩放的整数再运算，
 *     全程不碰浮点乘法。这样 JS 与 SQL `round(cents::numeric * rate)` **逐位一致**，
 *     不会出现「同一天两次跑批差 1 分」。
 *  2. **缺汇率 → `null`**。绝不返回 0、绝不退化成 1:1（1:1 会让「换算失败」看起来
 *     像「汇率没变」）。同理，超出安全整数范围也返回 `null` 而不是近似值。
 *  3. **结转（carry-forward）必须显式**。ECB 参考汇率周末与 TARGET 假日不发布
 *     （实测 2025-01-01 整行缺失）。业务日 D 取「不晚于 D 的最近一个发布日」，
 *     并把 `carriedForward` 与 `rateDate` 一并交给调用方 —— 否则「12-26 的 USD 合计」
 *     看起来是当天价，实际是 12-24 的价。
 */

import type { Cents, CurrencyCode, IsoDate } from "./metrics.js";

/** 汇率小数位上限，与 DDL `rate numeric(20,10)` 一致。 */
export const FX_SCALE_DIGITS = 10;

/** 10^10，缩放因子。 */
const FX_SCALE = 10n ** BigInt(FX_SCALE_DIGITS);

/** `YYYY-MM-DD` 形状校验（业务日）。 */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 一条汇率发布记录（对应 `fx_rates` 一行）。
 * `rate` 用十进制字符串，1 `base` = `rate` `quote`。
 */
export interface FxQuote {
  /** ECB **实际发布日**（不是业务日）。 */
  readonly asOfDate: IsoDate;
  readonly base: CurrencyCode;
  readonly quote: CurrencyCode;
  readonly rate: string;
}

/** 某个业务日实际采用的汇率（含结转信息）。 */
export interface FxConversion {
  readonly base: CurrencyCode;
  readonly quote: CurrencyCode;
  readonly rate: string;
  /** 实际采用的汇率发布日；**不等于**业务日时说明发生了结转。 */
  readonly rateDate: IsoDate;
  readonly carriedForward: boolean;
}

/**
 * 十进制字符串 → 10^10 缩放整数。
 *
 * - `"0.6939293000"` → `6939293000n`；
 * - 小数位超过 10 位时，**仅当多出的位全是 0** 才截断（`"1.00000000010"` 可），
 *   否则返回 `null`（不静默丢精度）；
 * - 空串、非数字、负数、科学计数法一律 `null`。
 */
export function parseFxRateToScaled(text: string): bigint | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const matched = /^(\d+)(?:\.(\d*))?$/.exec(trimmed);
  if (matched === null) return null;

  const integerPart = matched[1] ?? "";
  const fractionPart = matched[2] ?? "";
  if (fractionPart.length > FX_SCALE_DIGITS) {
    const extra = fractionPart.slice(FX_SCALE_DIGITS);
    if (extra.replaceAll("0", "") !== "") return null;
  }
  const paddedFraction = fractionPart.slice(0, FX_SCALE_DIGITS).padEnd(FX_SCALE_DIGITS, "0");
  return BigInt(integerPart) * FX_SCALE + BigInt(paddedFraction);
}

/**
 * 业务日 → 该日实际生效的汇率（不晚于业务日的最近一次发布）。
 *
 * - 业务日当天有发布 → `carriedForward = false`；
 * - 当天无发布（周末/假日）→ 取最近的前一个发布日，`carriedForward = true`；
 * - 业务日之前没有任何发布 → `null`（**不是** 0、**不是** 1:1）。
 *
 * 只比较 `YYYY-MM-DD` 字符串（字典序即时间序），不做时区推断 —— 业务日由
 * `AUTHORITATIVE_BUSINESS_DAY_SOURCE` 决定，本函数不越权。
 */
export function selectFxQuote(
  quotes: readonly FxQuote[],
  businessDay: IsoDate,
  pair?: { readonly base: CurrencyCode; readonly quote: CurrencyCode },
): FxQuote | null {
  if (!ISO_DATE_PATTERN.test(businessDay)) return null;
  let best: FxQuote | null = null;
  for (const quote of quotes) {
    if (!ISO_DATE_PATTERN.test(quote.asOfDate)) continue;
    if (quote.asOfDate > businessDay) continue;
    if (pair !== undefined && (quote.base !== pair.base || quote.quote !== pair.quote)) continue;
    // 同一发布日出现重复行时保持「先到者」，调用方负责去重（DDL 主键已保证唯一）。
    if (best === null || quote.asOfDate > best.asOfDate) best = quote;
  }
  return best;
}

/** 把选中的发布记录转成折算依据（附 `carriedForward`）。 */
export function toFxConversion(quote: FxQuote, businessDay: IsoDate): FxConversion {
  return {
    base: quote.base,
    quote: quote.quote,
    rate: quote.rate,
    rateDate: quote.asOfDate,
    carriedForward: quote.asOfDate !== businessDay,
  };
}

/**
 * 整数分 × 汇率 → 整数分（**半程远离零**，与 Postgres `round(numeric)` 一致）。
 *
 * 返回 `null` 的全部情形：汇率不可解析、汇率 ≤ 0、`cents` 非安全整数、
 * 结果超出安全整数范围。**调用方必须把 `null` 渲染成「—」/「缺汇率」**。
 */
export function convertCents(cents: Cents, conversion: Pick<FxConversion, "rate">): Cents | null {
  if (!Number.isSafeInteger(cents)) return null;
  const scaledRate = parseFxRateToScaled(conversion.rate);
  if (scaledRate === null || scaledRate <= 0n) return null;

  const negative = cents < 0;
  const magnitude = BigInt(negative ? -cents : cents);
  const rounded = (magnitude * scaledRate + FX_SCALE / 2n) / FX_SCALE;
  const signed = negative ? -rounded : rounded;
  if (signed > BigInt(Number.MAX_SAFE_INTEGER) || signed < BigInt(Number.MIN_SAFE_INTEGER)) return null;
  return Number(signed);
}

/**
 * 一组折算结果对外回显用的**代表汇率日** = 最早（最陈旧）的那个 `rateDate`。
 *
 * 为什么要「最早」而不是「最新」：同一响应里若 A 币结转自 12-24、B 币结转自 12-26，
 * 把 12-26 写进 `meta.fxRateDate` 会让人以为所有数字都基于 12-26 的价。
 * 取最早值是保守且不会高估新鲜度的做法；空集合 → `null`。
 */
export function representativeRateDate(conversions: readonly FxConversion[]): IsoDate | null {
  let oldest: IsoDate | null = null;
  for (const conversion of conversions) {
    if (!ISO_DATE_PATTERN.test(conversion.rateDate)) continue;
    if (oldest === null || conversion.rateDate < oldest) oldest = conversion.rateDate;
  }
  return oldest;
}

/**
 * 折算并求和（**先折算、后求和，全程只取整一次**）。
 *
 * 「先按日折算再求和」≠「先求和再折算」，两者可差数分；本函数固定前者。
 * 任一项缺汇率 → 整体返回 `null`，不静默跳过缺项（跳过会让合计偏小且无人察觉）。
 */
export function sumConvertedCents(
  entries: readonly { readonly cents: Cents; readonly conversion: Pick<FxConversion, "rate"> }[],
): Cents | null {
  let total = 0;
  for (const entry of entries) {
    const converted = convertCents(entry.cents, entry.conversion);
    if (converted === null) return null;
    total += converted;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}
